import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import {
  BaseInput,
  Button,
  ButtonSize,
  ButtonVariant
} from "shared/components";
import { AsyncSelect } from "shared/components/AsyncSelect/AsyncSelect";
import { SaveCancelButtons } from "shared/app/SaveCancelButtons";
import { Row } from "views/applications-id-report/components/Row";
import { SectionCard } from "shared/app/SectionCard";
import { Unit } from "types/landlord/address";
import addressApi from "api/landlord/address";
import { lockboxesApi, loadLockboxOptions } from "api/landlord/lockboxes";
import { getErrorMessage } from "utils/error";

interface LockboxSectionProps {
  unit: Unit;
  propertyId: number;
}

const EditLockboxForm = ({
  unit,
  propertyId,
  onClose
}: {
  unit: Unit;
  propertyId: number;
  onClose: () => void;
}) => {
  const [selectedLockboxId, setSelectedLockboxId] = useState<number | null>(
    unit.lockbox?.id ?? null
  );
  const [selectedLockboxLabel, setSelectedLockboxLabel] = useState(
    unit.lockbox?.name ?? ""
  );
  const queryClient = useQueryClient();

  const { data: selectedLockbox } = useQuery({
    queryKey: ["LOCKBOX", selectedLockboxId],
    queryFn: () => lockboxesApi.getLockbox(selectedLockboxId!),
    enabled: Boolean(selectedLockboxId)
  });

  const { mutate: save, isPending } = useMutation({
    mutationKey: ["UNIT", "UPDATE_LOCKBOX"],
    mutationFn: () =>
      addressApi.editUnit(unit.id, { lockbox_id: selectedLockboxId }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["unit", String(propertyId), String(unit.id)]
      });
      queryClient.invalidateQueries({
        queryKey: ["properties", String(propertyId)]
      });
      onClose();
      toast.success("Lockbox updated successfully");
    },
    onError: (err) => {
      toast.error(getErrorMessage(err));
    }
  });

  return (
    <SectionCard title="Lockbox">
      <div className="space-y-4">
        <div className="flex gap-4">
          <div className="flex-1">
            <AsyncSelect
              id="lockbox"
              label="Lockbox"
              value={selectedLockboxId || ""}
              isClearable
              searchable
              hideOptional
              options={
                selectedLockboxId && selectedLockboxLabel
                  ? [{ value: selectedLockboxId, label: selectedLockboxLabel }]
                  : []
              }
              // @ts-expect-error
              loadOptions={loadLockboxOptions}
              additional={{ page: 1, propertyId }}
              placeholder="Select lockbox"
              onChange={(e) => {
                if (!Array.isArray(e)) {
                  const val = e.target.value;
                  setSelectedLockboxId(val ? Number(val) : null);
                  setSelectedLockboxLabel(val ? (e.label ?? "") : "");
                }
              }}
            />
          </div>
          <BaseInput
            id="admin_code"
            label="Admin code"
            className="flex-1"
            value={selectedLockbox?.code ?? ""}
            onChange={() => {}}
            disabled
            hideOptional
          />
        </div>
        <SaveCancelButtons
          onSave={() => save()}
          onCancel={onClose}
          saveLoading={isPending}
          saveDisabled={isPending}
        />
      </div>
    </SectionCard>
  );
};

export const LockboxSection = ({ unit, propertyId }: LockboxSectionProps) => {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing)
    return (
      <EditLockboxForm
        unit={unit}
        propertyId={propertyId}
        onClose={() => setIsEditing(false)}
      />
    );

  return (
    <SectionCard
      title="Lockbox"
      headerExtra={
        <Button
          size={ButtonSize.SM}
          variant={ButtonVariant.BASE}
          onClick={() => setIsEditing(true)}
        >
          Edit
        </Button>
      }
    >
      <div className="space-y-4">
        <Row title="Lockbox" value={unit.lockbox?.name ?? "--"} />
        <Row title="Admin code" value={unit.lockbox?.code ?? "--"} />
      </div>
    </SectionCard>
  );
};
